import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import {
    LoadingOverlay,
} from "@mantine/core";
import {
    createFolder,
    updateFolder,
    addFolderItem,
    removeFolderItem,
    deleteFolder,
    deleteReport,
    deleteChart,
    deleteDashboard,
    deleteForm,
    getFolderForItem,
    deleteTabGroup,
} from "@bi-studio/shared/apis";
import { LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import type { SelectedItem as FolderActionSelectedItem } from "@/components/folders/FolderActionToolbar";
import {
    MoveItemsModal,
    RootFolderGrid,
    DeleteConfirmModal,
    CreateFolderModal,
} from "@/components/folders";
import { PageActions, OpenPlaygroundButton, AddNewEntityButton, PageContainer, ItemDetailModal } from "@/components";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    fetchFolderContent,
    selectCurrentFolder,
    selectChildFolders,
    selectFolderLoading,
    selectFolderError,
} from "@/store/slices/folderSlice";

export function SubfolderView() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { folderId } = useParams();

    const dispatch = useAppDispatch();

    // Redux State
    const folder = useAppSelector(selectCurrentFolder);
    const childFolders = useAppSelector(selectChildFolders);
    const isLoading = useAppSelector(selectFolderLoading);
    const error = useAppSelector(selectFolderError);

    const [itemToMove, setItemToMove] = React.useState<FolderActionSelectedItem | null>(null);
    const [itemToDelete, setItemToDelete] = React.useState<FolderActionSelectedItem | null>(null);
    const [detailItem, setDetailItem] = React.useState<FolderActionSelectedItem | null>(null);

    const [showCreateFolderModal, setShowCreateFolderModal] = React.useState(false);
    const [isCreatingFolder, setIsCreatingFolder] = React.useState(false);

    React.useEffect(() => {
        if (!token || !folderId) return;

        dispatch(fetchFolderContent({ token, folderId }));

        // Listen for refresh events
        const handleRefresh = () => {
            dispatch(fetchFolderContent({ token, folderId }));
        };
        window.addEventListener("refresh-folders", handleRefresh);

        return () => {
            window.removeEventListener("refresh-folders", handleRefresh);
        };
    }, [token, folderId, dispatch]);

    const handleBack = () => {
        const parentId = folder?.parentFolderId || (folder as any)?.folder?.parentFolderId;
        if (parentId) {
            navigate(`/folders/${parentId}`);
        } else {
            navigate("/folders");
        }
    };



    const handleItemClick = (
        type: "report" | "dashboard" | "chart" | "form" | "tab_group" | "folder",
        id: string
    ) => {
        if (type === "report") {
            navigate(`/reports/${id}`);
        } else if (type === "chart") {
            navigate(`/charts/${id}`);
        } else if (type === "form") {
            navigate(`/forms/${id}`);
        } else if (type === "tab_group") {
            navigate(`/tab-groups/${id}`);
        } else if (type === "folder") {
            navigate(`/folders/${id}`);
        } else {
            navigate(`/dashboards/${id}`);
        }
    };

    const handleMove = async (targetFolderId: string | null) => {
        if (!token || !folder || !itemToMove) return;

        try {
            if (itemToMove.type === "folder") {
                // Move folder by updating parentFolderId
                await updateFolder(token, itemToMove.id, { parentFolderId: targetFolderId });
            } else {
                // For items in a folder:
                // 1. Remove from current folder
                await removeFolderItem(token, folderId!, itemToMove.type, itemToMove.id);

                // 2. Add to target folder (if not root)
                if (targetFolderId) {
                    await addFolderItem(token, targetFolderId, {
                        itemType: itemToMove.type,
                        itemId: itemToMove.id,
                    });
                }
            }
        } catch (err) {
            alert(`Failed to move: ${err instanceof Error ? err.message : "Failed"}`);
        }

        // Refresh
        setItemToMove(null);
        window.dispatchEvent(new CustomEvent("refresh-folders"));
        dispatch(fetchFolderContent({ token, folderId: folderId! }));
    };

    const handleDelete = async () => {
        if (!token || !itemToDelete) return;

        let deletedDashboardFolderId: string | null = null;

        try {
            if (itemToDelete.type === "folder") {
                await deleteFolder(token, itemToDelete.id);
            } else if (itemToDelete.type === "report") {
                await deleteReport(token, itemToDelete.id);
            } else if (itemToDelete.type === "chart") {
                await deleteChart(token, itemToDelete.id);
            } else if (itemToDelete.type === "dashboard") {
                const folderInfo = await getFolderForItem(token, "dashboard", itemToDelete.id);
                deletedDashboardFolderId = folderInfo.folderId;
                await deleteDashboard(token, itemToDelete.id);
            } else if (itemToDelete.type === "form") {
                await deleteForm(token, itemToDelete.id);
            } else if (itemToDelete.type === "tab_group") {
                await deleteTabGroup(token, itemToDelete.id);
            }
        } catch (err) {
            alert(`Failed to delete: ${err instanceof Error ? err.message : "Failed"}`);
        }

        // Refresh
        setItemToDelete(null);
        window.dispatchEvent(new CustomEvent("refresh-folders"));
        dispatch(fetchFolderContent({ token, folderId: folderId! }));

        // Navigate to folder if a dashboard was deleted and it was in a folder
        if (itemToDelete.type === "dashboard" && deletedDashboardFolderId) {
            navigate(`/folders/${deletedDashboardFolderId}`);
        }
    };

    const handleCreateFolder = async (folderName: string) => {
        if (!token || !folderId) return;

        try {
            setIsCreatingFolder(true);
            await createFolder(token, { name: folderName, parentFolderId: folderId });
            // Reload folders
            dispatch(fetchFolderContent({ token, folderId }));
            window.dispatchEvent(new CustomEvent("refresh-folders"));
        } finally {
            setIsCreatingFolder(false);
        }
    };
    if (error && !isLoading) {
        return (
            <ErrorState
                message={error}
                onAction={() => {
                    if (token && folderId) {
                        dispatch(fetchFolderContent({ token, folderId }));
                    }
                }}
                actionLabel="Retry"
            />
        );
    }

    const actualFolderId = folder ? ((folder as any).folder?.id || folder.id) : null;
    const isReady = folder && actualFolderId === folderId;

    if (!isReady) {
        return <LoadingScreen message="Loading folder content..." />;
    }

    return (
        <PageContainer>
            {/* Loading Overlay */}
            <LoadingOverlay visible={isLoading && isReady} />

            {/* Content */}
            <PageActions
                title={folder!.name || (folder as any)?.folder?.name}
                onBack={handleBack}
            >
                <OpenPlaygroundButton onClick={() => navigate("/playground")} />
                <AddNewEntityButton folderId={actualFolderId} onNewFolder={() => setShowCreateFolderModal(true)} />
            </PageActions>

            <RootFolderGrid
                topLevelFolders={childFolders}
                rootReports={folder!.reports || []}
                rootDashboards={folder!.dashboards || []}
                rootCharts={folder!.charts || []}
                rootForms={(folder as any).forms || []}
                rootTabGroups={folder!.tabGroups || []}
                onMove={(type, id) => setItemToMove({ type: type as any, id })}
                onDelete={(type, id) => setItemToDelete({ type: type as any, id })}
                onViewDetails={(type, id) => setDetailItem({ type: type as any, id })}
                onItemClick={handleItemClick}
                emptyStateText="This folder is empty"
            />

            {itemToMove && folder && (
                <MoveItemsModal
                    selectedItems={[itemToMove]}
                    currentFolderId={folderId!}
                    onMove={handleMove}
                    onClose={() => setItemToMove(null)}
                />
            )}

            <DeleteConfirmModal
                opened={itemToDelete !== null}
                onClose={() => setItemToDelete(null)}
                onDelete={handleDelete}
                selectedCount={itemToDelete ? 1 : 0}
            />

            <CreateFolderModal
                opened={showCreateFolderModal}
                onClose={() => setShowCreateFolderModal(false)}
                onSubmit={handleCreateFolder}
                loading={isCreatingFolder}
            />



            <ItemDetailModal
                opened={detailItem !== null}
                onClose={() => setDetailItem(null)}
                itemType={detailItem?.type as any}
                itemId={detailItem?.id ?? null}
            />
        </PageContainer>
    );
}
